import Viewer from '@/widget/Editor/Viewer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/esm/Spinner'
import Badge from 'react-bootstrap/Badge'
import Stack from 'react-bootstrap/Stack'
import { useNavigate, useParams } from 'react-router-dom'
import { fetchCompany, setBookmark, getPerm } from './data'
import type { Company, Perm, BookmarkParam } from './data'
import { cursorPointer, companyName } from './style.css'
import {
  PencilLineIcon,
  MousePointerClickIcon,
  CheckLineIcon,
  ArrowLeftIcon,
  LinkIcon,
} from 'lucide-react'
// import { useEffect } from 'react'
import clsx from 'clsx'

export default function CompanyViewer() {
  const navigate = useNavigate()

  const { data: permission } = useQuery<Perm>({
    queryKey: ['company', 'perm'],
    queryFn: getPerm,
    staleTime: Infinity,
  })

  const { id } = useParams<{ id: string }>()
  const companyId = Number(id)
  const {
    data: company,
    isLoading: loadingCompany,
    isError: companyError,
  } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => fetchCompany(companyId),
    enabled: Number.isFinite(companyId),
  })

  const queryClient = useQueryClient()

  const bookmarkMutation = useMutation({
    mutationFn: setBookmark,
    onMutate: ({ companyId, action }: BookmarkParam) => {
      const queryKey = ['company', companyId]
      queryClient.cancelQueries({ queryKey })
      const prevCompany = queryClient.getQueryData<Company>(queryKey)
      queryClient.setQueryData<Company>(queryKey, (company: Company | undefined) => {
        if (!company) return
        return {
          ...company,
          bookmarked: action === 'ADD',
        }
      })
      return { prevCompany }
    },
    onError: (err, vars, ctx) => {
      console.log('update bookmark failed', err)
      queryClient.setQueryData<Company>(['company', vars.companyId], ctx?.prevCompany)
    },
    /*
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] })
    },
    */
  })

  /*
  useEffect(() => {
    if (pathname.endsWith('view')) window.scrollTo({ top: 0, behavior: 'instant' })
  }, [pathname])
  */

  return (
    <>
      <div className="d-flex justify-content-between">
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center"
          onClick={() => {
            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1)
            } else {
              navigate('/company', { replace: true })
            }
          }}
        >
          <ArrowLeftIcon className="me-1" />
          戻る
        </Button>
        {permission?.canWrite && (
          <Button
            variant="outline-secondary"
            className="d-flex align-items-center"
            onClick={() => {
              if (company) navigate(`/company/${company.id}/edit`, { replace: true })
            }}
          >
            <PencilLineIcon className="me-1" />
            編集
          </Button>
        )}
      </div>

      <Card className={`my-4 ${cursorPointer}`}>
        {loadingCompany ? (
          <Spinner />
        ) : companyError ? (
          <Alert variant="error">Error</Alert>
        ) : !company ? (
          <Alert variant="error">Error</Alert>
        ) : (
          <Card.Body>
            <Card.Title className="d-flex align-items-center">
              <span className={clsx(companyName, 'me-2')}>{company.name}</span>
              {company.url && (
                <LinkIcon
                  onClick={() => window.open(company.url, '_blank')}
                  className="link-primary"
                />
              )}
            </Card.Title>
            <div>
              <ul>
                {company.summary?.map((summaryItem, idx) => (
                  <li key={idx} className="text-muted">
                    {summaryItem}
                  </li>
                ))}
              </ul>
            </div>
            <Stack direction="horizontal" gap={2} className="mb-2 flex-wrap">
              {company.industries?.map((industry) => (
                <Badge bg="secondary" key={industry.id}>
                  {industry.name}
                </Badge>
              ))}
            </Stack>
            <Viewer content={company.intro} />
            <Card.Footer className="d-flex justify-content-center">
              <Button
                variant={company.bookmarked ? 'primary' : 'outline-secondary'}
                className="d-flex align-items-center"
                onClick={() => {
                  bookmarkMutation.mutate({
                    companyId: company.id,
                    action: company.bookmarked ? 'REMOVE' : 'ADD',
                  })
                }}
              >
                {company.bookmarked ? (
                  <CheckLineIcon className="me-1" />
                ) : (
                  <MousePointerClickIcon className="me-1" />
                )}
                {company.bookmarked ? 'チェック済み' : '興味あり'}
              </Button>
            </Card.Footer>
          </Card.Body>
        )}
      </Card>
    </>
  )
}
